import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import AlertTitle from '@mui/material/AlertTitle'
import Stack from '@mui/material/Stack'
import TablePagination from '@mui/material/TablePagination'
import Typography from '@mui/material/Typography'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableRow from '@mui/material/TableRow'
import Alert from '@mui/material/Alert'
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar'
import CarDialogForm from './car-dialog-form'
import { useGetCarsByBuildingIdQuery } from '@/api/whiteList'
import TableHeader from '@/components/TableHeader'
import { accessCarsTableHead } from '@/utils/stub'
import { useSelector } from 'react-redux'
import { selectActiveBuildingId } from '@/features/building/buildingSlice'
import WidgetPaper from '@/components/WidgetPaper'
import TableEmptyPlaceholder from '@/components/TableEmptyPlaceholder'
import SearchInput from '@/components/SearchInput'
import AddButton from '../access/AddButton'
import Divider from '@mui/material/Divider'

const CarsList = ({ref}) => {
  const activeBuildingId = useSelector(selectActiveBuildingId);
  const [openDialog, setOpenDialog] = useState(false)
  const { t } = useTranslation()
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(0)
  const [apiPage, setApiPage] = useState(1)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const emptyPlaceholder = `${t('list_empty')}\n${t('cars-list_explained')}`
  const {
    data: cars,
    isSuccess,
    isLoading,
    isError
  } = useGetCarsByBuildingIdQuery({
    buildingId: activeBuildingId,
    page: apiPage,
    size: rowsPerPage,
    plate_number: search
  })
  const [orderBy, setOrderBy] = useState('number')
  const [order, setOrder] = useState('asc')
  const [selected, setSelected] = useState([])

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc'
    setOrder(isAsc ? 'desc' : 'asc')
    setOrderBy(property)
  }

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = cars?.data?.map((item) => item.id)
      setSelected(newSelecteds)
      return
    }
    setSelected([])
  }

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  const handleChangePage = (event, newPage) => {
    setPage(newPage)
    if (newPage > page) {
      setApiPage((prev) => prev + 1)
    } else {
      setApiPage((prev) => prev - 1)
    }
  }

  return (
    <WidgetPaper
      ref={ref}
      title={t('vehicles')}
    >
      <Stack>
        <Stack
          direction="row"
          spacing={1}
          sx={{
            alignItems: "center",
            flex: 1,
            minWidth: 0,
            mb: 2
          }}>
          <SearchInput
            placeholder={t('enter_number')}
            onChange={(value) => setSearch(value)}
            delay={300}
            disabled={!cars?.data?.length && !search}
          />
          <Divider orientation="vertical" flexItem />
          <AddButton onClick={() => setOpenDialog(true)} />
        </Stack>
        <CarDialogForm
          open={openDialog}
          onClose={() => setOpenDialog(false)}
        />
        <TableContainer>
          <Table stickyHeader>
            <TableHeader
              order={order}
              orderBy={orderBy}
              headLabel={accessCarsTableHead}
              rowCount={cars?.data?.length}
              numSelected={selected.length}
              onRequestSort={handleRequestSort}
              onSelectAllClick={handleSelectAllClick}
            />
            <TableBody>
              {cars?.data?.map((car) => {
                const isItemSelected = selected.indexOf(car.id) !== -1

                return (
                  <TableRow
                    key={car?.id}
                    tabIndex={-1}
                    role="checkbox"
                    selected={isItemSelected}
                    aria-checked={isItemSelected}
                  >
                    <TableCell component="th" scope="row" padding="none">
                      <Typography variant="subtitle2" noWrap>
                        {car?.id}
                      </Typography>
                    </TableCell>
                    <TableCell align="left">{car?.brand}</TableCell>
                    <TableCell align="left">{car?.model}</TableCell>
                    <TableCell align="left">{car?.plate_number}</TableCell>
                    <TableCell align="left">{car?.phone}</TableCell>
                    <TableCell align="left">{car?.parking_numbers}</TableCell>
                    <TableCell align="left">{car?.flat}</TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </TableContainer>
        { isSuccess && !cars?.data?.length &&(
          <TableEmptyPlaceholder
            icon={DirectionsCarIcon}
            message={emptyPlaceholder}
          />
        )}
        {isSuccess && (
          <TablePagination
            rowsPerPageOptions={[ 10, 25 ]}
            component="div"
            count={cars?.metadata?.total_count || 0}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        )}
      </Stack>
      {isError && (
        <Alert severity="error" sx={{ mt: 3 }}>
          <AlertTitle>{t('alert')}</AlertTitle>
          {t('error_occurred')}
        </Alert>
      )}
    </WidgetPaper>
  );
};
export default CarsList;
